// redux/slices/consent.ts
//
// NOTE: this file isn't wired into your root reducer yet — add
// `consent: consentReducer` to whatever combineReducers/configureStore
// call assembles your store, same as the usession-supabase slice.

import {
  CONSENT_POLICY_VERSION,
  ConsentCategory,
  ConsentCookieValue,
} from "@/components/user/PrivacyConsent/cookie";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// Mirrors the ReduxSupabaseUSessionRootState pattern already used for the
// usession slice, so components can useSelector without a combined
// app-wide RootState type.
export interface ReduxConsentRootState {
  consent: ConsentState;
}

export interface ConsentState {
  essential: true;
  analytics: boolean;
  sessionReplay: boolean;
  policyVersion: number;
  hasResponded: boolean;
  isPreferencesOpen: boolean;
}

const initialState: ConsentState = {
  essential: true,
  analytics: false,
  sessionReplay: true,
  policyVersion: CONSENT_POLICY_VERSION,
  hasResponded: false,
  isPreferencesOpen: false,
};

const consentSlice = createSlice({
  name: "consent",
  initialState,
  reducers: {
    // Called once on mount if a valid cookie already exists.
    hydrate(state, action: PayloadAction<ConsentCookieValue>) {
      state.analytics = action.payload.analytics;
      state.sessionReplay = action.payload.sessionReplay;
      state.policyVersion = action.payload.policyVersion;
      state.hasResponded = true;
    },
    acceptAll(state) {
      state.analytics = true;
      state.sessionReplay = true;
      state.hasResponded = true;
    },
    rejectNonEssential(state) {
      state.analytics = false;
      state.sessionReplay = false;
      state.hasResponded = true;
    },
    setCategory(
      state,
      action: PayloadAction<{ key: ConsentCategory; value: boolean }>,
    ) {
      state[action.payload.key] = action.payload.value;
      state.hasResponded = true;
    },
    openPreferences(state) {
      state.isPreferencesOpen = true;
    },
    closePreferences(state) {
      state.isPreferencesOpen = false;
    },
  },
});

export const {
  hydrate,
  acceptAll,
  rejectNonEssential,
  setCategory,
  openPreferences,
  closePreferences,
} = consentSlice.actions;
export default consentSlice.reducer;
